import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import API from '../utils/api';
import { getSliderImage, responsiveImage } from '../utils/imageHelper';

const AUTOPLAY_MS = 5500;

const defaultSlides = [
  {
    _id: 'd1',
    subtitle: 'Natural wood · Non-toxic finish',
    title: 'Toys that grow with your child',
    description: 'Montessori-inspired wooden toys for every stage, newborn to 12+.',
    cta: 'Shop all toys',
    link: '/products',
    image: 'https://images.unsplash.com/photo-1596461404969-9ae70f2830c1',
  },
  {
    _id: 'd2',
    subtitle: 'Shop by age',
    title: 'The right toy for every milestone',
    description: 'Developmentally designed toys, matched to your child’s age.',
    cta: 'Toys for 1–2 years',
    link: '/shop-by-age/1-2-years',
    image: 'https://images.unsplash.com/photo-1617331140180-e8262094733a',
  },
  {
    _id: 'd3',
    subtitle: 'Gifting made easy',
    title: 'Gifts they will treasure',
    description: 'Birthdays, baby showers and festivals — gift wrapping available.',
    cta: 'Shop bestsellers',
    link: '/products?featured=true',
    image: 'https://images.unsplash.com/photo-1545558014-8692077e9b5c',
  },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Compact promotional banner slider. The parent controls width/height
 * via `className` so the banner can sit beside other content.
 */
const HeroSlider = ({ className = '' }) => {
  const [slides, setSlides] = useState(defaultSlides);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  // Only download a slide's image once it is shown (or about to be), not all up front
  const [loaded, setLoaded] = useState(() => new Set([0]));
  const touchX = useRef(null);

  useEffect(() => {
    let alive = true;
    API.get('/sliders/active')
      .then(({ data }) => { if (alive && data.sliders?.length) { setSlides(data.sliders); setCurrent(0); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const count = slides.length;
  const go = useCallback((i) => setCurrent(((i % count) + count) % count), [count]);
  const next = useCallback(() => go(current + 1), [go, current]);
  const prev = useCallback(() => go(current - 1), [go, current]);

  useEffect(() => {
    setLoaded(s => (s.has(current) && s.has((current + 1) % count) ? s : new Set([...s, current, (current + 1) % count])));
  }, [current, count]);

  useEffect(() => {
    if (paused || count < 2 || prefersReducedMotion()) return;
    const t = setTimeout(next, AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [current, paused, count, next]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchX.current = null;
  };

  return (
    <section
      className={`relative overflow-hidden rounded-2xl bg-espresso ${className}`}
      aria-roledescription="carousel"
      aria-label="Offers and collections"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, i) => {
        const active = i === current;
        return (
          <div
            key={slide._id || i}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${active ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${count}`}
            aria-hidden={!active}
          >
            {loaded.has(i) && (
              <img
                {...responsiveImage(getSliderImage(slide), undefined, '3:1')}
                sizes="(min-width: 1280px) 1216px, 100vw"
                alt={slide.title}
                width="1440" height="480"
                className="absolute inset-0 w-full h-full object-cover"
                fetchpriority={i === 0 ? 'high' : 'low'}
                decoding={i === 0 ? 'sync' : 'async'}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />

            <div className={`relative h-full flex flex-col justify-center p-5 sm:p-8 lg:p-12 max-w-[85%] sm:max-w-[60%] transition-opacity ${active ? 'opacity-100 duration-500 delay-300' : 'opacity-0 duration-150'}`}>
              {slide.subtitle && (
                <p className="font-sans text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] uppercase text-gold-200 mb-2">
                  {slide.subtitle}
                </p>
              )}
              <p className="font-serif text-white leading-[1.1] text-xl sm:text-3xl lg:text-[2.5rem] mb-2 sm:mb-3">
                {slide.title}
              </p>
              {slide.description && (
                <p className="hidden sm:block font-sans text-sm text-white/85 leading-relaxed mb-5 max-w-sm">
                  {slide.description}
                </p>
              )}
              <Link to={slide.link || '/products'} tabIndex={active ? 0 : -1}
                className="self-start inline-flex items-center gap-2 mt-2 sm:mt-0 bg-white text-espresso font-sans font-semibold text-xs sm:text-sm px-4 py-2.5 sm:px-6 sm:py-3 rounded-full transition hover:bg-gold-100">
                {slide.cta || 'Shop now'} <FaArrowRight className="text-[10px]" />
              </Link>
            </div>
          </div>
        );
      })}

      {count > 1 && (
        <div className="absolute bottom-4 left-5 right-5 sm:left-8 sm:right-8 lg:left-12 z-20 flex items-center justify-between">
          <div className="flex">
            {slides.map((s, i) => (
              // 24px tap area around a small pill indicator
              <button key={s._id || i} type="button" onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}`} aria-current={i === current}
                className="group/dot h-6 min-w-[24px] flex items-center justify-center">
                <span className={`block h-1.5 rounded-full transition-all duration-300 ${i === current ? 'w-7 bg-white' : 'w-1.5 bg-white/50 group-hover/dot:bg-white/80'}`} />
              </button>
            ))}
          </div>
          <div className="hidden sm:flex gap-2">
            <button type="button" onClick={prev} aria-label="Previous slide"
              className="w-9 h-9 rounded-full bg-white/20 backdrop-blur text-white flex items-center justify-center transition hover:bg-white hover:text-espresso">
              <FaChevronLeft className="text-xs" />
            </button>
            <button type="button" onClick={next} aria-label="Next slide"
              className="w-9 h-9 rounded-full bg-white/20 backdrop-blur text-white flex items-center justify-center transition hover:bg-white hover:text-espresso">
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSlider;
